'use client';

import { useCallback, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useClerk, useSignIn } from '@clerk/nextjs';
import { useSendVerificationCode } from '@lib/auth/hooks/use-send-verification-code';
import { CLERK_PASSWORD_VALIDATION_ERROR_NAME, ClerkPasswordValidationError } from '@lib/clerk/errors/clerk-password-validation-error';
import { APP_ROUTES } from '@lib/constants/apis';
import { useMutation, useQueryClient } from '@lib/query/client';

/** Non-secret shell claim data rendered during the email OTP step. */
export interface ShellAccountClaimState {
  email: string;
  fullName: string;
  firstName: string;
  lastName: string;
}

/** Original signup credentials held only in memory until the OTP is verified. */
interface ShellAccountClaimCredentials extends ShellAccountClaimState {
  password: string;
}

/** Minimal public claim response used to activate the upgraded Clerk identity. */
interface ClaimShellAccountResponse {
  success: boolean;
  signInToken?: string;
  error?: string;
  reason?: 'invalid_code' | 'too_many_attempts' | 'password_validation';
}
const INVALID_SHELL_CLAIM_CODE_ERROR = 'InvalidShellClaimCodeError';
const EXISTING_ACCOUNT_MESSAGE = 'An account with this email already exists. Sign in with Google or your password.';

/** Expected wrong/expired OTP failure that the global MutationCache does not report. */
class InvalidShellClaimCodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = INVALID_SHELL_CLAIM_CODE_ERROR;
  }
}

/** Claims an OTP-proven shell identity and applies the original signup credentials. */
async function claimShellAccount(params: ShellAccountClaimCredentials & {
  code: string;
}): Promise<ClaimShellAccountResponse> {
  const {
    email,
    code,
    firstName,
    lastName,
    password
  } = params;
  const response = await fetch('/api/auth/verification/email/verify-code', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email,
      code,
      shellClaim: {
        firstName,
        lastName,
        password
      }
    })
  });
  const data = (await response.json()) as ClaimShellAccountResponse;
  if (!response.ok) {
    const message = data.error || 'Failed to set up account';
    if (data.reason === 'invalid_code') {
      throw new InvalidShellClaimCodeError(message);
    }
    if (data.reason === 'password_validation') {
      throw new ClerkPasswordValidationError(message);
    }
    throw new Error(message);
  }
  return data;
}

/** Owns shell-account OTP verification, promotion, session activation, and navigation. */
export function useShellAccountSignupClaim(postClaimHref: string = APP_ROUTES.HOME) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const clerk = useClerk();
  const {
    signIn
  } = useSignIn();
  const sendCode = useSendVerificationCode();
  const claimAccount = useMutation({
    mutationFn: claimShellAccount,
    // Wrong/expired OTPs are expected user input; infrastructure and Clerk
    // failures remain reportable through the global MutationCache.
    meta: {
      suppressSentryErrorNames: [INVALID_SHELL_CLAIM_CODE_ERROR, CLERK_PASSWORD_VALIDATION_ERROR_NAME]
    }
  });
  const [claim, setClaim] = useState<ShellAccountClaimState | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  // Keep the submitted password out of rendered state while the OTP UI owns
  // the flow; it is cleared as soon as the claim succeeds.
  const credentialsRef = useRef<ShellAccountClaimCredentials | null>(null);
  const beginClaim = useCallback(async (params: ShellAccountClaimCredentials) => {
    setError(null);
    setPasswordError(null);
    try {
      const sent = await sendCode.mutateAsync({
        email: params.email,
        shellOnly: true
      });
      if (sent.codeSent === false) {
        throw new Error(EXISTING_ACCOUNT_MESSAGE);
      }
      credentialsRef.current = params;
      setClaim({
        email: params.email,
        fullName: params.fullName,
        firstName: params.firstName,
        lastName: params.lastName
      });
      setCode('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
      throw err;
    }
  }, [sendCode]);
  const resendCode = useCallback(async () => {
    if (!claim) return;
    setError(null);
    try {
      const sent_0 = await sendCode.mutateAsync({
        email: claim.email,
        shellOnly: true
      });
      if (sent_0.codeSent === false) {
        setError(EXISTING_ACCOUNT_MESSAGE);
        return;
      }
    } catch (err_0: unknown) {
      setError(err_0 instanceof Error ? err_0.message : 'Failed to send verification code');
    }
  }, [claim, sendCode]);

  /** Returns to the populated signup form and discards the current OTP attempt. */
  const cancelClaim = useCallback(() => {
    credentialsRef.current = null;
    setClaim(null);
    setCode('');
    setError(null);
    setPasswordError(null);
  }, []);
  const verifyAndSignIn = useCallback(async (submittedCode: string) => {
    const credentials = credentialsRef.current;
    if (!claim || !credentials || !signIn) return false;
    setError(null);
    setIsCompleting(true);
    try {
      const result = await claimAccount.mutateAsync({
        ...credentials,
        code: submittedCode
      });
      if (!result.signInToken) {
        setError('Account setup succeeded without a sign-in token');
        return false;
      }
      const {
        error: ticketError
      } = await signIn.create({
        strategy: 'ticket',
        ticket: result.signInToken
      });
      if (ticketError) {
        setError(ticketError.message || 'Sign in failed');
        return false;
      }
      if (signIn.status !== 'complete') {
        setError('Sign in incomplete');
        return false;
      }
      const {
        error: finalizeError
      } = await signIn.finalize();
      if (finalizeError) {
        setError(finalizeError.message || 'Session activation failed');
        return false;
      }
      try {
        await clerk.user?.reload();
      } catch {
        // Session activation succeeded; profile freshness is best-effort.
      }
      credentialsRef.current = null;
      queryClient.clear();
      router.push(postClaimHref);
      return true;
    } catch (err_1: unknown) {
      if (err_1 instanceof ClerkPasswordValidationError) {
        // Clerk leaves the OTP unconsumed, but return to the form so the
        // rejected password cannot be submitted again without correction.
        credentialsRef.current = null;
        setClaim(null);
        setCode('');
        setPasswordError(err_1.message);
      } else {
        setError(err_1 instanceof Error ? err_1.message : 'Account setup failed');
      }
      return false;
    } finally {
      setIsCompleting(false);
    }
  }, [claim, signIn, claimAccount, clerk, queryClient, router, postClaimHref]);
  return {
    claim,
    code,
    setCode,
    error,
    passwordError,
    isSendingCode: sendCode.isPending,
    isVerifying: claimAccount.isPending || isCompleting,
    beginClaim,
    cancelClaim,
    resendCode,
    verifyAndSignIn
  };
}